/**
 * useStudentChatPracticeEnabled - Reads `feature.student_chat_practice`, the switch of the
 * practices the study tutor proposes (TANAI-2616).
 *
 * Fail-closed: `true` ONLY when the backend explicitly confirms `value === true`. Loading,
 * unauthenticated, absent key, error or any other value → `false`, so a proposal card never shows
 * on a tenant that has not switched it on. The backend applies the same rule (a missing key is
 * off), and every proposal endpoint also answers 404 when the flag is off.
 *
 * Audience: AI-enabled students (`isPersistedAiStudent`), the only users with a study chat.
 * Everyone else gets `false` without any network traffic.
 */
import configurationsService from '../services/configurationsService';
import { isPersistedAiStudent } from '../services/serviceConfig';
import { createSessionConfigHook } from './createSessionConfigHook';

export const STUDENT_CHAT_PRACTICE_CONFIG_KEY = 'feature.student_chat_practice';

type ConfigurationResult = {
  status: string;
  value: unknown;
};

async function fetchStudentChatPracticeEnabled(): Promise<boolean> {
  const result = (await configurationsService.getConfiguration(STUDENT_CHAT_PRACTICE_CONFIG_KEY)) as ConfigurationResult;
  return result?.status === 'ok' && result.value === true;
}

const useStudentChatPracticeConfig = createSessionConfigHook<boolean>({
  fetchValue: fetchStudentChatPracticeEnabled,
  defaultValue: false,
  isEligible: isPersistedAiStudent,
});

function useStudentChatPracticeEnabled(): boolean {
  return useStudentChatPracticeConfig().value;
}

export default useStudentChatPracticeEnabled;
